import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft,
  Upload,
  Trash2,
  Save,
  Eye,
  EyeOff,
  Star,
  Loader2,
  X,
  Check,
  Image as ImageIcon,
  FolderUp,
  CheckCircle,
  AlertCircle,
  FileImage
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Progress } from "../../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "../../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../App";
import OptimizedImage from "../../components/OptimizedImage";

const CATEGORIES = [
  { value: "wedding", label: "Wedding" },
  { value: "portrait", label: "Portrait" },
  { value: "event", label: "Event" },
  { value: "commercial", label: "Commercial" },
  { value: "nature", label: "Nature" },
  { value: "general", label: "General" }
];

const AdminGalleryDetail = () => {
  const { galleryId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  
  // Store token in state to ensure consistency during component lifecycle
  const [adminToken, setAdminToken] = useState(() => localStorage.getItem("admin_token"));
  
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Bulk upload state
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "general",
    is_public: true,
    is_featured: false,
    tags: ""
  });

  // Refresh token when component mounts or when localStorage changes
  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (token !== adminToken) {
      setAdminToken(token);
    }
  }, []);

  useEffect(() => {
    fetchGallery();
  }, [galleryId]);

  const fetchGallery = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/galleries/${galleryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setGallery(response.data);
      setFormData({
        title: response.data.title || "",
        description: response.data.description || "",
        category: response.data.category || "general",
        is_public: response.data.is_public ?? true,
        is_featured: response.data.is_featured ?? false,
        tags: (response.data.tags || []).join(", ")
      });
    } catch (error) {
      toast.error("Failed to load gallery");
      navigate("/admin/galleries");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("admin_token");
      await axios.put(`${API}/admin/galleries/${galleryId}`, {
        ...formData,
        tags: formData.tags.split(",").map(t => t.trim()).filter(Boolean)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Gallery saved");
      fetchGallery();
    } catch (error) {
      toast.error("Failed to save gallery");
    } finally {
      setSaving(false);
    }
  };

  // Drag and drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === dropZoneRef.current) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files).filter(f => 
      f.type.startsWith('image/') || f.type.startsWith('video/')
    );
    
    if (files.length > 0) {
      setSelectedFiles(prev => [...prev, ...files]);
      setBulkUploadOpen(true);
    }
  }, []);

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      setSelectedFiles(prev => [...prev, ...files]);
      setBulkUploadOpen(true);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const clearFiles = () => {
    setSelectedFiles([]);
    setUploadResults(null);
  };

  const handleBulkUpload = async () => {
    if (selectedFiles.length === 0) return;

    setUploading(true);
    setUploadProgress(0);
    setUploadResults(null);

    const results = { success: 0, failed: 0, errors: [] };
    const totalFiles = selectedFiles.length;
    const CHUNK_SIZE = 3; // Upload 3 files at a time

    try {
      const token = localStorage.getItem("admin_token");
      
      // Process in chunks for better performance
      for (let i = 0; i < selectedFiles.length; i += CHUNK_SIZE) {
        const chunk = selectedFiles.slice(i, i + CHUNK_SIZE);
        
        const uploadPromises = chunk.map(async (file) => {
          const formDataUpload = new FormData();
          formDataUpload.append("files", file);
          
          try {
            await axios.post(`${API}/admin/galleries/${galleryId}/upload`, formDataUpload, {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "multipart/form-data"
              }
            });
            return { success: true, filename: file.name };
          } catch (error) {
            return { 
              success: false, 
              filename: file.name, 
              error: error.response?.data?.detail || "Upload failed" 
            };
          }
        });

        const chunkResults = await Promise.all(uploadPromises);
        
        chunkResults.forEach(result => {
          if (result.success) {
            results.success++;
          } else {
            results.failed++;
            results.errors.push(`${result.filename}: ${result.error}`);
          }
        });

        // Update progress
        const completed = Math.min(i + CHUNK_SIZE, totalFiles);
        setUploadProgress(Math.round((completed / totalFiles) * 100));
      }

      setUploadResults(results);

      if (results.success > 0) {
        toast.success(`Uploaded ${results.success} file(s)`);
        fetchGallery();
      }
      
      if (results.failed > 0) {
        toast.error(`${results.failed} file(s) failed to upload`);
      }

    } catch (error) {
      toast.error("Upload failed");
      setUploadResults({ success: 0, failed: selectedFiles.length, errors: ["Upload failed"] });
    } finally {
      setUploading(false);
    }
  };

  const toggleAssetSelection = (assetId) => {
    setSelectedAssets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(assetId)) {
        newSet.delete(assetId);
      } else {
        newSet.add(assetId);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    if (selectedAssets.size === gallery?.assets?.length) {
      setSelectedAssets(new Set());
    } else {
      setSelectedAssets(new Set(gallery?.assets?.map(a => a.asset_id)));
    }
  };

  const deleteSelectedAssets = async () => {
    if (selectedAssets.size === 0) return;

    setSaving(true);
    try {
      const token = localStorage.getItem("admin_token");
      const deletePromises = Array.from(selectedAssets).map(assetId =>
        axios.delete(`${API}/admin/galleries/${galleryId}/assets/${assetId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      );
      
      await Promise.all(deletePromises);
      
      toast.success(`Deleted ${selectedAssets.size} photo(s)`);
      setSelectedAssets(new Set());
      setDeleteDialogOpen(false);
      fetchGallery();
    } catch (error) {
      toast.error("Failed to delete photos");
    } finally {
      setSaving(false);
    }
  };

  const setCoverPhoto = async (filename) => {
    try {
      const token = localStorage.getItem("admin_token");
      await axios.put(`${API}/admin/galleries/${galleryId}`, {
        cover_photo: filename
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Cover photo updated");
      fetchGallery();
    } catch (error) {
      toast.error("Failed to update cover photo");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div 
      className="space-y-6"
      ref={dropZoneRef}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drag overlay */}
      <AnimatePresence>
        {isDragging && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-primary/20 backdrop-blur-sm flex items-center justify-center"
          >
            <div className="bg-card border-2 border-dashed border-primary rounded-lg p-12 text-center">
              <FolderUp className="w-16 h-16 mx-auto text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-2">Drop files to upload</h3>
              <p className="text-muted-foreground">Release to add to gallery</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin/galleries")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-serif">{gallery?.title || "Gallery"}</h1>
            <p className="text-sm text-muted-foreground">
              {gallery?.assets?.length || 0} photos
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*"
            className="hidden"
            onChange={handleFileSelect}
          />
          <Button 
            variant="outline" 
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Photos
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Settings Panel */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h2 className="text-lg font-semibold mb-4">Gallery Settings</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>
            
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="category">Category</Label>
              <select
                id="category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-input bg-background"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <Label htmlFor="tags">Tags (comma separated)</Label>
              <Input
                id="tags"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              />
            </div>
            
            <div className="flex items-center justify-between pt-2">
              <Label htmlFor="is_public">Public Gallery</Label>
              <Switch
                id="is_public"
                checked={formData.is_public}
                onCheckedChange={(checked) => setFormData({ ...formData, is_public: checked })}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label htmlFor="is_featured">Featured on Homepage</Label>
              <Switch
                id="is_featured"
                checked={formData.is_featured}
                onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Photos Section */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Photos</h2>
          
          {gallery?.assets?.length > 0 && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={selectAll}>
                {selectedAssets.size === gallery?.assets?.length ? "Deselect All" : "Select All"}
              </Button>
              
              {selectedAssets.size > 0 && (
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete ({selectedAssets.size})
                </Button>
              )}
            </div>
          )}
        </div>

        {!gallery?.assets?.length ? (
          <div 
            className={`text-center py-12 border-2 border-dashed rounded-lg transition-colors ${
              isDragging ? 'border-primary bg-primary/5' : 'border-border'
            }`}
          >
            <ImageIcon className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No photos yet</h3>
            <p className="text-muted-foreground mb-4">
              Drag and drop photos here, or click to upload
            </p>
            <Button onClick={() => fileInputRef.current?.click()}>
              <Upload className="w-4 h-4 mr-2" />
              Upload Photos
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {gallery.assets.map((asset) => (
              <div 
                key={asset.asset_id}
                className={`relative aspect-square rounded-lg overflow-hidden group cursor-pointer border-2 transition-colors ${
                  selectedAssets.has(asset.asset_id) 
                    ? "border-primary" 
                    : "border-transparent hover:border-border"
                }`}
                onClick={() => toggleAssetSelection(asset.asset_id)}
              >
                <OptimizedImage
                  src={`${API}/admin/galleries/${galleryId}/media/${asset.filename}?token=${adminToken}`}
                  fallbackSrc={`${API}/admin/galleries/${galleryId}/media/${asset.filename}?token=${adminToken}`}
                  alt={asset.original_filename || asset.filename}
                  aspectRatio=""
                  className="w-full h-full"
                  maxRetries={2}
                />
                
                {/* Selection indicator */}
                <div className={`absolute top-2 left-2 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                  selectedAssets.has(asset.asset_id)
                    ? "bg-primary text-primary-foreground"
                    : "bg-black/40 text-white"
                }`}>
                  {selectedAssets.has(asset.asset_id) ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <span className="w-4 h-4 border-2 rounded-full" />
                  )}
                </div>
                
                {/* Cover indicator */}
                {gallery.cover_photo === asset.filename && (
                  <div className="absolute top-2 right-2 bg-yellow-500 text-black text-xs px-2 py-1 rounded font-medium flex items-center gap-1">
                    <Star className="w-3 h-3" fill="currentColor" />
                    Cover
                  </div>
                )}
                
                {/* Set as cover button */}
                {gallery.cover_photo !== asset.filename && (
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCoverPhoto(asset.filename);
                      }}
                    >
                      Set as Cover
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bulk Upload Dialog */}
      <Dialog open={bulkUploadOpen} onOpenChange={(open) => {
        if (!uploading) {
          setBulkUploadOpen(open);
          if (!open) clearFiles();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderUp className="w-5 h-5" />
              Upload Photos
            </DialogTitle>
            <DialogDescription>
              {selectedFiles.length} file(s) selected for upload
            </DialogDescription>
          </DialogHeader>

          {/* File List */}
          <div className="space-y-4">
            {!uploadResults && selectedFiles.length > 0 && (
              <div className="max-h-60 overflow-y-auto space-y-2">
                {selectedFiles.map((file, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <FileImage className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium truncate max-w-[300px]">{file.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    {!uploading && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeFile(index)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Upload Progress */}
            {uploading && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <Progress value={uploadProgress} />
              </div>
            )}

            {/* Upload Results */}
            {uploadResults && (
              <div className="space-y-3">
                <div className="flex items-center gap-4">
                  {uploadResults.success > 0 && (
                    <div className="flex items-center gap-2 text-green-500">
                      <CheckCircle className="w-5 h-5" />
                      <span>{uploadResults.success} uploaded</span>
                    </div>
                  )}
                  {uploadResults.failed > 0 && (
                    <div className="flex items-center gap-2 text-red-500">
                      <AlertCircle className="w-5 h-5" />
                      <span>{uploadResults.failed} failed</span>
                    </div>
                  )}
                </div>
                
                {uploadResults.errors.length > 0 && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <p className="text-sm font-medium text-red-400 mb-2">Errors:</p>
                    <ul className="text-sm text-red-300 space-y-1">
                      {uploadResults.errors.slice(0, 5).map((err, i) => (
                        <li key={i} className="truncate">• {err}</li>
                      ))}
                      {uploadResults.errors.length > 5 && (
                        <li>...and {uploadResults.errors.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Add More Files */}
            {!uploading && !uploadResults && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-4 h-4 mr-2" />
                Add More Files
              </Button>
            )}
          </div>

          <DialogFooter>
            {uploadResults ? (
              <Button onClick={() => {
                setBulkUploadOpen(false);
                clearFiles();
              }}>
                Done
              </Button>
            ) : (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setBulkUploadOpen(false);
                    clearFiles();
                  }}
                  disabled={uploading}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleBulkUpload}
                  disabled={uploading || selectedFiles.length === 0}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload {selectedFiles.length} File(s)
                    </>
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Photos</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedAssets.size} photo(s)? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={deleteSelectedAssets} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete Photos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminGalleryDetail;
